import React, { useState } from 'react';
import { Settings as SettingsIcon, Folder, ChevronDown } from 'lucide-react';
import useStore from '@/store';
import ProjectBottomSheet from '@/components/ui/ProjectBottomSheet';
import SettingsBottomSheet from '@/components/ui/SettingsBottomSheet';

interface TopControlsProps {
    handleBack: () => void;
    logout: () => void;
    isAuthenticated: boolean;
    // When true, render only the logo + wordmark (no project chip, no
    // action buttons). Used during onboarding / paywall, where nothing
    // in the full navbar is actionable until the subscription is set up.
    minimal?: boolean;
}

// Shared bar chrome: subtle top-to-bottom yellow gradient + hairline
// bottom border via inset box-shadow + soft drop shadow for
// elevation. Replaces the older flat `bg-yellow-400/50 + border-b-2 +
// shadow-lg` look that competed with content underneath and felt
// chunky next to the new drawer chrome.
const barClassName =
    'flex items-center bg-gradient-to-b from-yellow-400 to-yellow-400/[0.96] relative z-[200]';

const barStyle: React.CSSProperties = {
    paddingTop: 'max(6px, env(safe-area-inset-top))',
    paddingBottom: '6px',
    paddingLeft: 'max(8px, env(safe-area-inset-left))',
    paddingRight: 'max(8px, env(safe-area-inset-right))',
    // Cascaded shadow chain mirroring the BottomDrawer top edge in
    // reverse:
    //   1. inset hairline at the bottom (replaces border-b-2)
    //   2. wide soft drop shadow → elevation
    //   3. tight dark shadow → crisp contact line
    boxShadow: [
        'inset 0 -1px 0 rgba(100, 75, 0, 0.45)',
        '0 4px 14px rgba(0, 0, 0, 0.18)',
        '0 1px 4px rgba(0, 0, 0, 0.12)',
    ].join(', '),
};

const logoStyle: React.CSSProperties = {
    width: 'clamp(40px, 10vw, 50px)',
    height: 'clamp(40px, 10vw, 50px)',
};

const wordmarkStyle: React.CSSProperties = {
    fontSize: 'clamp(18px, 4.6vw, 22px)',
    fontFamily: "'Source Sans Pro', sans-serif",
    // Tightened from +0.02em → -0.005em for a less '90s wordmark.
    letterSpacing: '-0.005em',
};

const betaBadgeStyle: React.CSSProperties = {
    fontSize: '9px',
    lineHeight: 1,
};

// Shared logo + wordmark + BETA badge cluster. BETA is styled to read
// against the yellow panel: black ink on a transparent black tint.
const Brand: React.FC = () => (
    <div className="flex items-center gap-2 flex-shrink-0 select-none">
        <img
            src="/icon-512.png"
            alt="PinCam"
            // Dark drop-shadow lifts the dark hexagonal aperture mark
            // off the yellow gradient — without it the silhouette
            // muddies into the background.
            className="flex-shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.45)]"
            style={logoStyle}
        />
        <div className="flex items-start gap-1.5">
            <h1
                className="font-bold text-black leading-none whitespace-nowrap"
                style={wordmarkStyle}
            >
                PinCam
            </h1>
            <span
                className="font-mono font-bold tracking-[0.14em] text-black border border-black/60 bg-black/[0.06] rounded-md px-1 py-0.5 mt-0.5"
                style={betaBadgeStyle}
                aria-label="Beta version"
            >
                BETA
            </span>
        </div>
    </div>
);

const TopControls: React.FC<TopControlsProps> = ({
    handleBack,
    logout,
    isAuthenticated,
    minimal = false,
}) => {
    const { currentProject } = useStore();
    const [showSettings, setShowSettings] = useState(false);
    const [showProjectPicker, setShowProjectPicker] = useState(false);

    if (!isAuthenticated) {
        return null;
    }

    // Keeping these handlers for future use.
    void handleBack;
    void logout;

    // Minimal variant: logo + wordmark + BETA badge only, centered.
    // Used on screens where the app chrome is not yet meaningful
    // (onboarding, paywall).
    if (minimal) {
        return (
            <div
                className={`${barClassName} justify-center`}
                style={barStyle}
            >
                <Brand />
            </div>
        );
    }

    return (
        <div
            className={`${barClassName} justify-between gap-2`}
            style={barStyle}
        >
            <Brand />

            {/* Active project pill — single entry point to switch
                projects. Folder icon + name (or placeholder) + chevron
                signal the tappable affordance. The picker opens as a
                bottom drawer (portaled, see ProjectBottomSheet). */}
            {/* Right cluster: project pill + Settings, sibling buttons
                in the same family. Single flex row with gap-2 so the
                spacing is consistent, both buttons share the same
                40-px height, and the whole cluster is right-anchored
                via the parent's justify-between. The pill truncates
                long project names; Settings stays square. */}
            <div className="flex items-center gap-2 min-w-0 ml-2">
                <button
                    type="button"
                    onClick={() => setShowProjectPicker(true)}
                    className="flex items-center gap-1.5 px-2.5 rounded-xl min-w-0 max-w-full bg-yellow-300/40 border border-black/30 hover:bg-yellow-300/60 active:bg-yellow-300/80 transition-colors shadow-sm"
                    style={{
                        height: 'min(8.5vw, 40px)',
                        minHeight: 'min(8.5vw, 40px)',
                    }}
                    title={
                        currentProject
                            ? `Project: ${currentProject.name} — tap to switch`
                            : 'Tap to pick a project'
                    }
                    aria-label="Switch project"
                    aria-haspopup="dialog"
                    aria-expanded={showProjectPicker}
                >
                    <Folder
                        className="text-black flex-shrink-0"
                        style={{
                            width: 'clamp(14px, 4vw, 18px)',
                            height: 'clamp(14px, 4vw, 18px)',
                        }}
                        aria-hidden="true"
                    />
                    <span
                        className="text-black font-bold truncate"
                        style={{ fontSize: 'clamp(13px, 4vw, 18px)' }}
                    >
                        {currentProject ? currentProject.name : 'Pick project'}
                    </span>
                    <ChevronDown
                        className="text-black/70 flex-shrink-0"
                        style={{
                            width: 'clamp(14px, 4vw, 18px)',
                            height: 'clamp(14px, 4vw, 18px)',
                        }}
                        aria-hidden="true"
                    />
                </button>

                <button
                    type="button"
                    onClick={() => setShowSettings(true)}
                    className="flex items-center justify-center bg-yellow-300/40 hover:bg-yellow-300/60 active:bg-yellow-300/80 border border-black/30 rounded-xl transition-colors flex-shrink-0 shadow-sm"
                    title="Settings"
                    aria-label="Settings"
                    aria-haspopup="dialog"
                    aria-expanded={showSettings}
                    style={{
                        width: 'min(8.5vw, 40px)',
                        height: 'min(8.5vw, 40px)',
                    }}
                >
                    <SettingsIcon
                        className="text-black"
                        style={{
                            width: 'clamp(18px, 4.6vw, 22px)',
                            height: 'clamp(18px, 4.6vw, 22px)',
                        }}
                    />
                </button>
            </div>

            {/* Portaled bottom drawers. They live outside the navbar's
                stacking context and own their backdrops + close logic. */}
            <ProjectBottomSheet
                open={showProjectPicker}
                onClose={() => setShowProjectPicker(false)}
            />
            <SettingsBottomSheet
                open={showSettings}
                onClose={() => setShowSettings(false)}
            />
        </div>
    );
};

export default TopControls;
